import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Input, Select, message, Tag, Row, Col, Card, Statistic, DatePicker } from 'antd';
import { ReloadOutlined, ArrowUpOutlined, ArrowDownOutlined, EyeOutlined } from '@ant-design/icons';
import { paymentAPI, customerAPI, supplierAPI } from '../../services/api';
import dayjs from 'dayjs';

const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

const PaymentList = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState({ type: '', customerId: '', supplierId: '' });
  const [dateRange, setDateRange] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [stats, setStats] = useState({});

  const typeMap = {
    'receipt': { text: '收款', color: 'green' },
    'payment': { text: '付款', color: 'red' }
  };

  const paymentMethodMap = {
    'cash': '现金',
    'bank': '银行转账',
    'wechat': '微信',
    'alipay': '支付宝',
    'check': '支票',
    'other': '其他'
  };

  const statusMap = {
    'pending': { text: '待确认', color: 'orange' },
    'confirmed': { text: '已确认', color: 'green' },
    'cancelled': { text: '已取消', color: 'default' }
  };

  const columns = [
    {
      title: '单号',
      dataIndex: 'paymentNo',
      key: 'paymentNo',
      width: 140
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 80,
      render: (text) => {
        const type = typeMap[text] || { text, color: 'default' };
        return <Tag color={type.color}>{type.text}</Tag>;
      }
    },
    {
      title: '客户/供应商',
      key: 'party',
      width: 150,
      render: (_, record) => {
        if (record.type === 'receipt') {
          return record.customer?.shortName || record.customer?.name || '-';
        } else {
          return record.supplier?.shortName || record.supplier?.name || '-';
        }
      }
    },
    {
      title: '关联单据',
      key: 'relatedBill',
      width: 140,
      render: (_, record) => {
        if (record.type === 'receipt') {
          return record.receivable?.billNo || '-';
        } else {
          return record.payable?.billNo || '-';
        }
      }
    },
    {
      title: '金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (text, record) => {
        const color = record.type === 'receipt' ? '#52c41a' : '#ff4d4f';
        const prefix = record.type === 'receipt' ? '+' : '-';
        return <span style={{ color, fontWeight: 'bold' }}>{prefix}¥{parseFloat(text).toFixed(2)}</span>;
      }
    },
    {
      title: '收付方式',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      width: 100,
      render: (text) => paymentMethodMap[text] || text
    },
    {
      title: '收付日期',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      width: 110,
      render: (text) => text ? dayjs(text).format('YYYY-MM-DD') : '-'
    },
    {
      title: '交易流水号',
      dataIndex: 'transactionNo',
      key: 'transactionNo',
      width: 140,
      render: (text) => text || '-'
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (text) => {
        const status = statusMap[text] || { text, color: 'default' };
        return <Tag color={status.color}>{status.text}</Tag>;
      }
    },
    {
      title: '操作人',
      dataIndex: ['operator', 'realName'],
      key: 'operatorName',
      width: 100,
      render: (text, record) => text || record.operator?.username || '-'
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      width: 150,
      ellipsis: true,
      render: (text) => text || '-'
    }
  ];

  useEffect(() => {
    fetchCustomers();
    fetchSuppliers();
    fetchStats();
  }, []);

  useEffect(() => {
    fetchPayments();
  }, [pagination.current, pagination.pageSize, searchText, filters, dateRange]);

  const fetchCustomers = async () => {
    try {
      const response = await customerAPI.getList({ limit: 1000 });
      setCustomers(response.data || []);
    } catch (error) {
      console.error('获取客户列表失败', error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await supplierAPI.getList({ limit: 1000 });
      setSuppliers(response.data || []);
    } catch (error) {
      console.error('获取供应商列表失败', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await paymentAPI.getStats();
      setStats(response);
    } catch (error) {
      console.error('获取统计失败', error);
    }
  };

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        search: searchText,
        ...filters
      };

      if (dateRange && dateRange[0] && dateRange[1]) {
        params.startDate = dateRange[0].format('YYYY-MM-DD');
        params.endDate = dateRange[1].format('YYYY-MM-DD');
      }

      const response = await paymentAPI.getList(params);

      setPayments(response.data);
      setPagination(prev => ({
        ...prev,
        total: response.pagination.total
      }));
    } catch (error) {
      message.error('获取收付款记录失败');
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize
    }));
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleReset = () => {
    setSearchText('');
    setFilters({ type: '', customerId: '', supplierId: '' });
    setDateRange(null);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>收付款记录</h2>
      </div>

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={4}>
          <Card>
            <Statistic
              title="收款总额"
              value={stats.totalReceipt || 0}
              prefix={<ArrowUpOutlined style={{ color: '#52c41a' }} />}
              precision={2}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="付款总额"
              value={stats.totalPayment || 0}
              prefix={<ArrowDownOutlined style={{ color: '#ff4d4f' }} />}
              precision={2}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic title="收款笔数" value={stats.receiptCount || 0} suffix="笔" />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic title="付款笔数" value={stats.paymentCount || 0} suffix="笔" />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="今日收款"
              value={stats.todayReceipt || 0}
              prefix="¥"
              precision={2}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="今日付款"
              value={stats.todayPayment || 0}
              prefix="¥"
              precision={2}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col>
            <Search
              placeholder="搜索单号"
              onSearch={handleSearch}
              style={{ width: 180 }}
              allowClear
            />
          </Col>
          <Col>
            <Select
              placeholder="类型"
              style={{ width: 100 }}
              allowClear
              value={filters.type || undefined}
              onChange={(value) => handleFilterChange('type', value)}
            >
              <Option value="receipt">收款</Option>
              <Option value="payment">付款</Option>
            </Select>
          </Col>
          {filters.type === 'receipt' && (
            <Col>
              <Select
                placeholder="选择客户"
                style={{ width: 150 }}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                value={filters.customerId || undefined}
                onChange={(value) => handleFilterChange('customerId', value)}
              >
                {customers.map(c => (
                  <Option key={c.id} value={c.id}>{c.shortName || c.name}</Option>
                ))}
              </Select>
            </Col>
          )}
          {filters.type === 'payment' && (
            <Col>
              <Select
                placeholder="选择供应商"
                style={{ width: 150 }}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                value={filters.supplierId || undefined}
                onChange={(value) => handleFilterChange('supplierId', value)}
              >
                {suppliers.map(s => (
                  <Option key={s.id} value={s.id}>{s.shortName || s.name}</Option>
                ))}
              </Select>
            </Col>
          )}
          <Col>
            <RangePicker
              value={dateRange}
              onChange={handleDateRangeChange}
              placeholder={['开始日期', '结束日期']}
            />
          </Col>
          <Col>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              重置
            </Button>
          </Col>
        </Row>
      </Card>

      <Table
        columns={columns}
        dataSource={payments}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条记录`
        }}
        onChange={handleTableChange}
        scroll={{ x: 1400 }}
      />
    </div>
  );
};

export default PaymentList;
